/* eslint-disable react/destructuring-assignment */
import { Heading, Image } from '@modusign/design-system';
import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { v4 } from 'uuid';

import LoadingStatus from '@libs/loading-status';

import approvalRequestImage from '@app/assets/modal/start-modal/image-approval-request.png';
import requestSigningImage from '@app/assets/modal/start-modal/image-request-signing.png';
import StartContractConfirmModal from '@app/components/Modal/StartContractConfirmModal';
import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import { usePDF } from '@app/hooks/usePDF';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import workspaceService from '@app/libs/workspaceService';
import { setDataLayerValues } from '@app/store/analytics/actions';
import { contractSetupContractDataUpdate, setExitRoute } from '@app/store/contract-setup/actions';
import { useCheckSigningRequestAuthority } from '@app/store/contract-setup/hooks/useCheckSigningRequestAuthority';
import { useSetupContractByEditingDocument } from '@app/store/contract-setup/hooks/useSetupContractByEditingDocument';
import { useLoadSigningStateByTemplate } from '@app/store/contract-setup/hooks/useSetupContractByTemplate';
import { ContractSetupExitRouteState } from '@app/store/contract-setup/reducers/exitRoute';
import { documentSetupSetupWithDraft } from '@app/store/documentSetup/actions';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { getUserAuthorityLoadingStatus } from '@app/store/user/authority/selectors';
import { getEmail, getName } from '@app/store/user/user/selectors';

interface StartFromApprovalsProps {
  fileId: string;
  startFrom: 'create-approvals';
  title: string;
  url: string;
}

interface StartFromApprovalsFromTemplateProps {
  templateId: string;
  startFrom: 'templates-approvals';
}

interface StartFromDocumentsProps {
  fileId: string;
  startFrom: 'documents';
  title: string;
  url: string;
}
// 서식작성 draft
interface StartFromDraftsProps {
  draftId: string;
  fileId: string;
  startFrom: 'drafts';
  title: string;
  url: string;
}

interface StartFromEditingDocumentsProps {
  id: string;
  startFrom: 'editingDocuments';
  title: string;
}

interface StartFromTemplatesProps {
  id: string;
  startFrom: 'templates';
}

export type ContractSetupStartConfirmModalProps =
  | StartFromApprovalsProps
  | StartFromApprovalsFromTemplateProps
  | StartFromDocumentsProps
  | StartFromDraftsProps
  | StartFromEditingDocumentsProps
  | StartFromTemplatesProps;

function toEntrypointQuery(props: ContractSetupStartConfirmModalProps) {
  const PREFIX = '?entryPoint=';

  if (props.startFrom === 'templates-approvals') {
    return `${PREFIX}create-approvals`;
  }

  if (props.startFrom === 'drafts') {
    return `${PREFIX}drafts:${props.draftId}`;
  }

  if (props.startFrom === 'editingDocuments' || props.startFrom === 'templates') {
    /**
     * 기존 구현을 유지하기 위해서 문서 편집기와 템플릿은
     * 기존 switch case대로 entryPoint를 documents로 고정한다.
     * FYI: https://github.com/modusign/modusign-client/blob/588508420abbc6bb2ee9b3e67c865af1808d8257/app/src/containers/Modal/ContractSetupStartConfirmModal/index.tsx#L85
     */
    return `${PREFIX}documents`;
  }

  if (props.startFrom) {
    return `${PREFIX}${props.startFrom}`;
  }

  return '';
}

function toExitRoute(
  startFrom: ContractSetupStartConfirmModalProps['startFrom']
): ContractSetupExitRouteState {
  switch (startFrom) {
    case 'editingDocuments':
      return '/editing-documents';
    case 'templates':
      return '/templates';
    default:
      return '/documents';
  }
}

function ContractSetupStartConfirmModal(props: ContractSetupStartConfirmModalProps) {
  const dispatch = useDispatch();
  const userAuthorityLoadingStatus = useSelector(getUserAuthorityLoadingStatus);
  const history = useHistory();

  const [isSigningStateLoading, setIsSigningStateLoading] = useState(false);

  const userEmail = useSelector(getEmail) as string;
  const userName = useSelector(getName) as string;

  const { setPDF, resetPDF } = usePDF();

  const { checkAuthority } = useCheckSigningRequestAuthority();
  const { load: loadByTemplate } = useLoadSigningStateByTemplate();
  const { load: loadByEditingDocument } = useSetupContractByEditingDocument();

  const { initialize } = useSigningState();

  const isLoading = userAuthorityLoadingStatus === LoadingStatus.PENDING || isSigningStateLoading;

  const handleModalClose = useCallback(() => {
    dispatch(modalClose());
  }, [dispatch]);

  const loadContractSetupByDefaultValue = useCallback(
    // (@Juyeong-Byeon) 아래 파라미터 타입은 다른 hook에서 미리 파일을 로드 하고, 해당 모달을 오픈합니다. 때문에, 요금제 체크 후 바로 기본 값을 로드해주면 됩니다.
    async (props: StartFromApprovalsProps | StartFromDocumentsProps | StartFromDraftsProps) => {
      const { title, url, fileId, startFrom } = props;

      resetPDF();
      initialize({
        attachments: [],
        document: {
          // (@DevSDK): document를 template data 핸들링하는데 사용할지 고려하기
          // FYI: https://modusign.slack.com/archives/C04902B1JF7/p1674694203898699
          title,
          pdfFile: {
            url,
            id: fileId,
          },
          pdfPassword: null,
        },
        fields: [],
        participants: [
          {
            type: 'REQUESTER',
            contact: userEmail,
            name: userName,
            id: v4(),
            dataLabel: v4(),
          },
          {
            type: 'SIGNER',
            locale: 'ko',
            contact: userEmail,
            signingMethod: 'EMAIL',
            name: userName,
            id: v4(),
            dataLabel: v4(),
            order: 1,
            index: 0,
          },
          {
            type: 'SIGNER',
            id: v4(),
            dataLabel: v4(),
            locale: 'ko',
            contact: '',
            name: '',
            signingMethod: null,
            order: 2,
            index: 1,
          },
        ],
        verifications: [],
        ui: {
          isInOrder: true,
          insertingField: null,
          lastInsertedField: createInitialLastInsertedField(),
        },
      });

      if (startFrom === 'documents') {
        pushDataLayer({ document: undefined });
        pushDataLayer({
          event: 'documentInitialized',
          document: {
            documentSource: 'fileUpload',
            startedAt: Date.now(),
          },
        });
      } else if (startFrom === 'drafts') {
        dispatch(
          documentSetupSetupWithDraft({ entryPoint: `${props.startFrom}:${props.draftId}` })
        );
        pushDataLayer({ document: undefined });
        pushDataLayer({
          event: 'documentInitialized',
          document: {
            documentSource: 'draft',
            startedAt: Date.now(),
          },
        });
      }
      const { data: pdf } = await getFileFromURL(url);
      setPDF(pdf);

      dispatch(contractSetupContractDataUpdate({ fileId, title, url }));
    },
    [dispatch, initialize, resetPDF, setPDF, userEmail, userName]
  );

  const handleStartContract = useCallback(async () => {
    try {
      await checkAuthority(workspaceService.getWorkspaceId());

      setIsSigningStateLoading(true);

      if (props.startFrom === 'editingDocuments') {
        const { id, title } = props;
        await loadByEditingDocument(id, title);
        dispatch(setDataLayerValues('서명요청', { 파일형식: 'pdf', 업로드파일출처: '문서편집기' }));
      } else if (props.startFrom === 'templates') {
        const { id } = props;
        await loadByTemplate({ templateId: id });
        dispatch(setDataLayerValues('서명요청', { 파일형식: 'pdf', 업로드파일출처: '템플릿' }));
      } else if (props.startFrom === 'templates-approvals') {
        const { templateId } = props;
        await loadByTemplate({ templateId });
      } else {
        await loadContractSetupByDefaultValue(props);
      }

      dispatch(modalClose());
      dispatch(setExitRoute(toExitRoute(props.startFrom)));
      history.push(`/editor/others-require/set${toEntrypointQuery(props)}`);
    } catch (error) {
      dispatch(
        modalOpen('CONFIRM', {
          title: '모두싸인 이용 안내',
          content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
        })
      );
    } finally {
      setIsSigningStateLoading(false);
    }
  }, [
    checkAuthority,
    dispatch,
    history,
    loadByEditingDocument,
    loadByTemplate,
    loadContractSetupByDefaultValue,
    props,
  ]);

  if (props.startFrom === 'create-approvals' || props.startFrom === 'templates-approvals') {
    return (
      <StartContractConfirmModal
        checkList={[
          `결재 완료 시 위 이름과 이메일로 서명이 요청됩니다.`,
          '서명 요청이 완료되면 서명 요청 1건이 차감됩니다.',
        ]}
        confirmButtonLabel="서명 요청 정보 설정"
        isLoading={isLoading}
        titleContent={
          <>
            <Image alt="" height="131" src={approvalRequestImage} width="354" />
            <Heading className="space-4x" sx={{ mt: 4 }}>
              내부결재 진행하기
            </Heading>
          </>
        }
        userEmail={userEmail}
        userName={userName}
        onModalClose={handleModalClose}
        onStartContract={handleStartContract}
      />
    );
  }

  return (
    <StartContractConfirmModal
      checkList={[
        '위 이름과 이메일로 서명이 요청됩니다.',
        '서명 요청이 완료되면 서명 요청 1건이 차감됩니다.',
      ]}
      confirmButtonLabel="시작하기"
      isLoading={isLoading}
      titleContent={
        <>
          <Image alt="" height="176" src={requestSigningImage} width="352" />
          <Heading className="space-4x">서명 요청</Heading>
        </>
      }
      userEmail={userEmail}
      userName={userName}
      onModalClose={handleModalClose}
      onStartContract={handleStartContract}
    />
  );
}

export default ContractSetupStartConfirmModal;
